import type React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type PageNoticeProps = {
    /** Iconify name. Semantic, not decorative — see `tone` below. */
    icon: string;
    /** Which meaning the icon carries. Drives its colour token. */
    tone: 'info' | 'warning';
    title: string;
    body: string;
    /** Optional way forward. A notice with no exit is a dead end. */
    action?: {
        label: string;
        to: string;
    };
    testId: string;
};

/**
 * The page-instead-of-page state: a kiosk session, an insufficient role, or a
 * tenant that hasn't been set up yet.
 *
 * In each of those cases there is nothing to show and nothing to do on the
 * screen itself, so this **replaces** the page rather than decorating it. The
 * self-resolvable cases carry a link, because a notice that only says "no" is
 * the failure this component exists to prevent.
 *
 * Shared by Ledger Settings and the Chart of Accounts: two surfaces behind the
 * same bos-api policy should not explain it two different ways.
 */
export const PageNotice: React.FC<PageNoticeProps> = ({
    icon,
    tone,
    title,
    body,
    action,
    testId,
}) => (
    <section
        className="flex max-w-xl flex-col items-start gap-3 rounded-xl bg-surface-secondary p-6"
        {...getNodeTestIdProp(testId)}
    >
        <Icon
            aria-hidden
            className={
                tone === 'warning'
                    ? 'text-2xl text-warning'
                    : 'text-2xl text-primary'
            }
            icon={icon}
        />
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        <p className="text-sm text-muted">{body}</p>
        {action ? (
            <Link
                className="text-sm font-medium underline underline-offset-2"
                to={action.to}
                {...getNodeTestIdProp(`${testId}-action`)}
            >
                {action.label}
            </Link>
        ) : null}
    </section>
);
